import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { Icon } from '@/common/components/Icon'
import { PageTitle } from '@/components'
import { Button, Input, Modal } from '@/components/ui'
import SelectField from '@/components/select-field'
import FormGroup from '@/core/components/FormGroup'
import {
  BadgeDto,
  BadgeTranslationDto,
  BadgeUpsertDto,
  PartnerDto,
} from '@/common/models/gamification'

import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { TranslationTabs } from './shared/TranslationTabs'

interface CreateBadgeModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  badge?: BadgeDto | null
  partners: PartnerDto[]
  onSubmit: (values: BadgeUpsertDto, id?: string) => void
  onDelete?: (id: string) => void
}

interface IBadgeFormValues {
  name: string
  iconRef: string
  partnerId: string
  translations: BadgeTranslationDto[]
}

export const CreateBadgeModal = ({
  open,
  onOpenChange,
  badge,
  partners,
  onSubmit,
  onDelete,
}: CreateBadgeModalProps) => {
  const isEditMode = !!badge
  const [deleteOpen, setDeleteOpen] = useState(false)

  const schema = yup.object().shape({
    name: yup.string().required('Name is required').max(128),
    iconRef: yup.string().max(256).default(''),
    partnerId: yup.string().default(''),
    translations: yup.array().default([]),
  })

  const defaultValues: IBadgeFormValues = {
    name: '',
    iconRef: '',
    partnerId: '',
    translations: [],
  }

  const {
    handleSubmit,
    register,
    control,
    formState: { errors, dirtyFields },
    reset,
    setValue,
  } = useForm<IBadgeFormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema) as never,
    defaultValues,
  })

  useEffect(() => {
    if (!open) return
    if (badge) {
      setValue('name', badge.name, { shouldValidate: true })
      setValue('iconRef', badge.iconRef ?? '', { shouldValidate: true })
      setValue('partnerId', badge.partnerId ?? '', { shouldValidate: true })
      setValue('translations', badge.translations ?? [], {
        shouldValidate: true,
      })
    }
    return () => {
      reset(defaultValues)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, badge])

  const handleClose = () => onOpenChange(false)

  const partnerOptions = [
    { value: 'none', label: 'None' },
    ...partners.map((p) => ({ value: p.id, label: p.name })),
  ]

  const submit = (values: IBadgeFormValues) => {
    onSubmit(
      {
        partnerId: values.partnerId || undefined,
        name: values.name,
        iconRef: values.iconRef || undefined,
        translations: values.translations ?? [],
      },
      badge?.id,
    )
    handleClose()
  }

  return (
    <>
      <Modal
        open={open}
        onOpenChange={(o) => {
          if (!o) handleClose()
        }}
        size="lg"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              icon="events-star"
              title={
                <span className="flex items-center gap-2">
                  {isEditMode ? 'Edit' : 'Add New'}{' '}
                  <span className="text-accent-1">Badge</span>
                </span>
              }
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            {isEditMode && onDelete ? (
              <Button
                onClick={() => setDeleteOpen(true)}
                variant="destructive"
                className="mr-auto"
              >
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                disabled={isEditMode && Object.keys(dirtyFields).length === 0}
                form="create-badge-form"
                type="submit"
                variant="primary"
              >
                Save
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="create-badge-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-6"
        >
          <FormGroup label="Name" error={errors?.name?.message}>
            <Input
              isInvalid={!!errors?.name?.message}
              {...register('name')}
              type="text"
              placeholder="Enter badge name"
            />
          </FormGroup>

          <FormGroup label="Icon Ref" error={errors?.iconRef?.message}>
            <Input
              isInvalid={!!errors?.iconRef?.message}
              {...register('iconRef')}
              type="text"
              placeholder="Enter icon reference"
            />
          </FormGroup>

          <Controller
            name="partnerId"
            control={control}
            render={({ field }) => (
              <SelectField
                label="Partner"
                placeholder="None"
                options={partnerOptions}
                value={field.value || 'none'}
                onValueChange={(v) => field.onChange(v === 'none' ? '' : v)}
              />
            )}
          />

          <Controller
            name="translations"
            control={control}
            render={({ field }) => (
              <TranslationTabs<BadgeTranslationDto>
                value={field.value}
                onChange={field.onChange}
                makeEmpty={(locale) => ({ locale, name: '' })}
                renderFields={({ value, onFieldChange }) => (
                  <FormGroup label="Name">
                    <Input
                      value={value.name ?? ''}
                      onChange={(e) => onFieldChange({ name: e.target.value })}
                      type="text"
                      placeholder="Enter translated name"
                    />
                  </FormGroup>
                )}
              />
            )}
          />
        </form>
      </Modal>

      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteOpen}
        setDeleteConfirmationModal={setDeleteOpen}
        deletedItem={badge?.name}
        deleteFunc={() => {
          if (badge && onDelete) onDelete(badge.id)
          handleClose()
        }}
      />
    </>
  )
}
